// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DealsService } from '@services/deals.service';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { PdftronInteractionsService } from '@services/pdftron-interactions.service';
import { UserService } from '@services/user.service';
import { bufferArrayToBase64, getHtmlCenter, isMobileOrTablet, scrollIntoView } from 'src/app/utils/utils';
import { iif, of, tap, takeWhile, switchMap, Observable, catchError, firstValueFrom, Subscription, forkJoin } from 'rxjs';
import { User, UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { FitModes } from 'src/app/pddj/document-form/document-form.interfaces';
import {
    AssigneeResolver,
    Assignees,
    DateTimeDoc,
    DdjDocument,
    DocumentStatus,
    FieldTypes,
    FormDocumentUploadType,
    GuidedSigningState,
    InternalDocumentTypeIds,
    PdfField,
} from '@interfaces/document.interfaces';
import { Deal, DealStatuses } from '@interfaces/deal.interfaces';
import {
    applyFields,
    applyImpactFields,
    applyReadOnlyFields,
    applyWatermark,
    cloneFields,
    handleFieldsVisibility,
    handlePreviousSigningWithSignatureMetaData,
    prepareFileForUpload,
    prepareFileForUploadBase64,
    removeAllAnnotations,
    setAutoPlacedFieldLocation,
} from 'src/app/utils/pdf-form-utils';
import { AppEvent, FileData } from '@interfaces/interfaces';
import { DeviceDetectorService } from 'ngx-device-detector';
import { IPService } from '@services/ip.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { VendorService } from '@services/vendor.service';
import { isIdentification } from 'src/app/utils/document-utils';
import { Features } from '@interfaces/dealer.interfaces';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'pdf-view',
    templateUrl: './pdf-view.component.html',
    styleUrls: ['./pdf-view.component.scss'],
})
export class PDFViewComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('pdfViewer', { static: true }) pdfViewerRef: ElementRef;
    @ViewChild('img', { static: true }) img: ElementRef;

    @Input() addField: any;
    @Input() editFormAction: AppEvent = null;
    @Input() fields: any = {};

    @Input() cancelEdit = false;
    @Input() dealerId: number = null;
    @Input() dealId: number = null;
    @Input() docId: number = null;
    @Input() document: any = null; // type could be DDJDocument (viewing) or File (uploading)
    @Input() fileNameToSave: string = null;
    @Input() documentError: any = null;
    @Input() template: string = null;
    @Input() allowGuidedSigning = false;
    @Input() allowPdfTronDownload = false;
    @Input() emitHasDocumentUrl = false;
    @Input() unauthenticatedUpload = false;
    @Input() commonDocument = false;
    @Output() public outEvent: EventEmitter<AppEvent> = new EventEmitter();

    @Input() file: any = null; // dealer create template/form
    @Input() dealerUploadedWithPredefineSignaturesFile: any = null; // for document type 6
    @Input() uploadFileDocumentType: InternalDocumentTypeIds = null;
    @Input() uploadTypeId: number = 0;

    public dateTime = '';
    public allowPdfEdit = false;
    public deal: Deal = null;
    private dealAnnotationCheck = false;
    public fileName: string = null;
    public fitMode: FitModes;
    public fitModes = FitModes;
    public hideButtons = false;
    public isComponentAlive = false;
    public isLoading = false;
    public mobileOrTablet = false;
    public pdfIsReadyShowButtons = false; // on small screens the buttons appear over the header before PDF
    private readyForSignatures = true; // always true for buyer now, will need to change when dealer uses this component
    public selectedImg: any = null;
    public selectedPdf: any = null;
    private signingAccess: Assignees = null;
    public typeOfCustomer: Assignees = null;
    public user: User = null;
    public currentUA = navigator.userAgent;
    private apiSigningEnabled = false;
    private isDesktop = false;
    public iFrameDoneLoading: boolean;
    private obs$: Subscription;
    public guidedSigningState: GuidedSigningState;
    public invalidSignatures = false;
    public alreadySignedSession: boolean = false;
    private ipAddress: string = null;
    DealStatuses = DealStatuses;
    DocumentStatus = DocumentStatus;
    private assigneeResolver: AssigneeResolver;
    private isWebviewerErrorState = false;

    guidedSigningOn$ = this.pits.isGuidedSigningOn.asObservable();

    get signingSaveAndContinue() {
        // - Document has been marked as complete
        // - We are on the last field to be signed
        //   - We check this two ways in case there are optional fields not completed
        return (
            this.pits.isDocumentComplete ||
            (this.guidedSigningState &&
                (this.guidedSigningState.fieldsToComplete <= 1 || this.guidedSigningState.fieldsToComplete - this.guidedSigningState.emptyOptionalChecks <= 1))
        );
    }

    get errorState(): boolean {
        return this.isWebviewerErrorState;
    }

    setIsErrorState(active: boolean) {
        this.isWebviewerErrorState = active;
    }

    get isDocumentReadyForSigning(): boolean {
        if (!this.document?.readyForSignatures && this.document?.uploadTypeId === FormDocumentUploadType.Manually_Filled_During_Deal.valueOf()) {
            return true;
        }

        if (
            !this.document?.readyForSignatures &&
            (this.document?.uploadTypeId === FormDocumentUploadType.Print_From_Impact_Form.valueOf() ||
                this.document?.uploadTypeId === FormDocumentUploadType.Print_From_PDF.valueOf())
        ) {
            return false;
        }

        if (this.template === 'upload') {
            return true;
        } else {
            return this.document?.readyForSignatures;
        }
    }

    constructor(
        private readonly dealsService: DealsService,
        private readonly deviceService: DeviceDetectorService,
        private readonly documentsService: DocumentsService,
        private readonly vendorService: VendorService,
        private readonly errorService: ErrorService,
        private readonly ipService: IPService,
        private readonly pits: PdftronInteractionsService,
        private readonly userService: UserService,
        private readonly zone: NgZone,
        readonly dateTimeDoc: DateTimeDoc,
        readonly featureFlag: FeatureFlagService
    ) {
        this.apiSigningEnabled = featureFlag.getDefaultFeatureConfiguration('apiSigning');
        //All Document Dates should be UTC Time.
        this.dateTime = dateTimeDoc.formatToUTC;

        this.isDesktop = this.deviceService.isDesktop();
        this.mobileOrTablet = !this.isDesktop;

        this.hideButtons =
            window.location.pathname.includes('forms') ||
            window.location.pathname.includes('add') ||
            window.location.pathname.includes('document-library-admin') ||
            window.location.pathname.includes('doc-library') ||
            window.location.pathname.includes('pddj');
        this.allowPdfEdit =
            window.location.pathname === '/dashboard/forms' ||
            window.location.pathname.includes('document-library-admin') ||
            (window.location.pathname.includes('add') && !window.location.pathname.includes('add/multiple'));
        this.isLoading = true;
        this.invalidSignatures = false;
        this.alreadySignedSession = false;
    }

    ngOnInit(): void {
        this.isComponentAlive = true;

        this.ipService
            .getIPAddress()
            .pipe(takeWhile(() => this.isComponentAlive === true))
            .subscribe((res) => (this.ipAddress = res.ip));

        //editing common document pdfs is only allowed on the document library admin page
        if (this.commonDocument && !window.location.pathname.includes('document-library-admin')) {
            this.allowPdfEdit = false;
        }

        if (this.unauthenticatedUpload === false && this.dealId) {
            this.dealsService
                .getDeal$(this.dealId)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((deal) => {
                    this.deal = deal;
                });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        //make sure guided signing auto move to next document refreshes properly
        if (changes?.documentError?.currentValue) {
            this.handleError(changes.documentError.currentValue);
            return;
        }

        if (changes.docId && changes.docId.previousValue !== changes.docId.currentValue) {
            this.guidedSigningState = undefined;
            this.pits.isDocumentComplete = false;
            this.pits.isGuidedSigningOn.next(false);
        }

        if (changes.document?.currentValue) {
            switch (this.template) {
                case 'sign':
                    this.loadDocumentToSign();
                    break;
                case 'upload':
                    this.loadWebViewerForUpload();
                    break;
                case 'view':
                    if (!this.docId) {
                        this.docId = this.document?.documentId;
                    }
                    this.loadDocumentToView();
                    break;
                case 'edit-existing':
                case 'edited-existing':
                case 'edit-deal-doc':
                    // editing a template that doesnt exist
                    if (!this.docId) {
                        this.loadWebViewerForDealerUpload();
                    }
                    // editing a template that exists
                    else {
                        this.loadWebViewerForDealerEditExistingTemplate();
                    }
                    break;
                case 'dealer-create':
                case 'edit-create':
                case 'multiple-create':
                    this.loadWebViewerForDealerUpload();
                    break;
                case 'multiple-edit':
                    this.loadDocumentFromPath();
                    break;
                case 'dealer-cleaned':
                case 'dealer-edited-existing':
                case 'dealer-view-template':
                    //viewing template that doesnt exist
                    if (!this.docId) {
                        this.loadWebViewerForDealerViewCreatedTemplate();
                    }
                    // viewing template that does exist
                    else {
                        const hasDocUrl = changes?.emitHasDocumentUrl?.currentValue ?? false;
                        this.loadWebViewerForDealerViewExistingTemplate(hasDocUrl);
                    }
                    break;
                case 'dealer-create-view':
                case 'cancel-create-edit':
                    if (!this.docId || (this.document?.size && this.docId)) {
                        this.loadWebViewerForDealerViewCreatedTemplate();
                    } else {
                        this.loadWebViewerForDealerViewExistingTemplate();
                    }
                    break;
                case 'cancel-existing-edit':
                    this.document = changes.document?.currentValue;
                    this.docId = this.document?.documentId ? this.document?.documentId : this.docId;
                    this.loadWebViewerForDealerEditTemplate();
                    break;
                default:
                    break;
            }
        }

        // only dealer sign is passing in this property, we need to check to see if there is
        // a currentValue && if that current value !== null
        // otherwise we will have 2 instances of WebViewer load on all other pages
        if (changes.dealerUploadedWithPredefineSignaturesFile?.currentValue && changes.dealerUploadedWithPredefineSignaturesFile?.currentValue !== null) {
            //No longer allowing ablitiy to sign during upload of document type 6.
            this.loadDocumentToSign();
            this.document.readyForSignatures = true;
        }

        if (changes.addField?.currentValue) {
            this.addFieldEvent(changes.addField.currentValue);
        }

        if (changes.editFormAction?.currentValue) {
            switch (changes.editFormAction.currentValue.context) {
                case 'clean':
                    this.removeAllFieldsAndAnnotations();
                    break;
                case 'set-editing':
                    this.setIsEditing(changes.editFormAction.currentValue.data?.impact);
                    break;
                case 'set-viewing':
                    this.setIsViewing(changes.editFormAction.currentValue.data);
                    break;
                case 'testing':
                    this.isTestingPdf(undefined, changes.editFormAction.currentValue.data?.impact);
                    break;
                case 'set-impact':
                    this.setIsEditing(changes.editFormAction.currentValue.data?.impact);
                    break;
                default:
                    break;
            }
        }

        this.dealAnnotationCheck = this.hasSignatures(this.document) === true && this.hasDealAnnotations(this.document) === true;
    }

    ngOnDestroy(): void {
        this.obs$.unsubscribe();
        // need to set this back to false for next document
        this.pits.iFrameLoaded.next(false);
        this.pits.pdfTron = null;
        this.pits.fields = {};
        this.pits.fieldsBkup = {};
        this.pits.baselineContent = null;
        this.pits.pdfViewerRef = null;
        this.pits.isDocumentComplete = false;
        this.pits.isGuidedSigningOn.next(false);
    }

    ngAfterViewInit(): void {
        this.obs$ = this.pits
            .isiFrameLoaded()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((iframeLoaded) => {
                this.iFrameDoneLoading = iframeLoaded;

                const el = document.getElementById('pdfViewerWrapper');
                el.style.visibility = this.iFrameDoneLoading ? 'visible' : 'hidden';

                if (iframeLoaded) {
                    this.pits.pdfTron.Core.documentViewer.addEventListener('mouseLeftDown', (event) => {
                        if (event.target.tagName.toLowerCase() === 'input' || event.target.className.includes('sign-here')) {
                            // Since the event is being triggered by PDFTron, Angular is not recognizing the
                            // change in data happening in guidedSigning(). By wrapping with zone.run() we
                            // tell Angular to start listening again which updates the view accordingly
                            this.zone.run(() => {
                                this.pits.turnGuidedSigningOn();

                                if (!this.guidedSigningState) {
                                    this.guidedSigningState = this.pits.getGuidedSigningState(this.signingAccess);
                                }
                            });
                        }
                    });

                    this.pits.pdfTron.UI.addEventListener('loaderror', () => {
                        //prevent spinning wheel on error.
                        this.isLoading = false;

                        //prevent save
                        this.setIsErrorState(true);
                        this.outEvent.emit({
                            context: 'isErrorState',
                            data: {},
                        });
                    });

                    this.pits.pdfTron.UI.addEventListener('documentLoaded', () => {
                        //prevent spinning wheel.
                        this.isLoading = false;
                    });
                }
            });
    }

    public addFieldEvent(fieldEvent: any): void {
        const pos = getHtmlCenter(this.pdfViewerRef.nativeElement, true);
        const field = this.pits.setTextAnnotFromDom(fieldEvent.assignee, fieldEvent.fieldType, pos);
        const currentPage = this.pits.pdfTron?.Core?.documentViewer?.getCurrentPage();

        // Only for when a user drags an annotation from one page to another
        if (field.PageNumber !== currentPage) {
            field.PageNumber = currentPage;
        }

        // Does its best to make sure auto clicking a field onto the screen does not
        // overlay the field on top of an existing one. There are limitations to what
        // We can do when we get to the bottom of a document.
        setAutoPlacedFieldLocation(this.pits.pdfTron, field);

        this.pits.addField(field);

        this.outEvent.emit({
            context: 'clear-field-event',
            data: {},
        });
    }

    public emitEvent(type: string, data: any = {}): void {
        this.outEvent.emit({
            context: type,
            data,
        });
    }

    public async cleanPdf(): Promise<void> {
        if (!this.isPdfAnnotationsAndFieldsResetAllowed()) {
            return;
        }

        this.removeAllFieldsAndAnnotations();
        this.pits.fields = {};
        this.pits.fieldsBkup = {};
    }

    public close(): void {
        this.outEvent.emit({
            context: 'close',
            data: {},
        });
    }

    public download() {
        let anchors = [];
        const links: Array<FileData> = [];

        prepareFileForUpload(this.pits.pdfTron);

        this.documentsService
            .getSignedFileByPath$(this.dealId, this.docId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((res) => {
                links.push(res.data);
                this.documentsService
                    .downloadLinks$(links)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((response) => {
                        anchors = response;
                        anchors[0].click();
                    });
            });
    }

    public rotatePDF() {
        const { documentViewer } = this.pits.pdfTron.Core;
        documentViewer.rotateClockwise();
    }

    public async downloadFromPdfTron() {
        if (this.allowPdfTronDownload && this.document) {
            const { documentViewer } = this.pits.pdfTron.Core;
            const doc = documentViewer.getDocument();
            const data = await doc.getFileData();
            const arr = new Uint8Array(data);
            const blob = new Blob([arr], { type: 'application/pdf' });

            const a: any = document.createElement('a');
            document.body.appendChild(a);
            a.style = 'display: none';
            const json = JSON.stringify(data);
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = this.document.fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        }
    }

    private async getSigningAccess() {
        const deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId));
        const user = this.userService.user;
        if (this.assigneeResolver === null || this.assigneeResolver === undefined) {
            this.assigneeResolver = new AssigneeResolver(
                user.userType.toString(),
                user.userId.toString(),
                user.email,
                user.dealerId.toString(),
                deal.salesperson.email,
                deal.dealerId.toString(),
                deal.buyer.userId.toString(),
                deal.coBuyer?.userId?.toString()
            );
        }

        this.signingAccess = this.assigneeResolver.assigneeType;
    }

    private async getThenSetDealInfo(deal?: Deal) {
        try {
            deal = await this.getDealInfo(deal);
            deal.listOfDocuments = this.getCustomerSignedDocs();
            return deal;
        } catch (ex) {
            console.error(ex);
            return deal;
        }
    }

    private getCustomerSignedDocs() {
        const signedDocs = <string[]>[];

        //listOfDocuments no longer gets all documents but only documents for that assignee. DDJ-3090
        this.documentsService.documents.forEach((doc) => {
            if (doc.status) {
                if (doc.status.buyerSignedBy && this.signingAccess == Assignees.buyer) {
                    signedDocs.push(doc.displayName);
                } else if (doc.status.coBuyerSignedBy && this.signingAccess == Assignees.cobuyer) {
                    signedDocs.push(doc.displayName);
                } else if (doc.status.dealerSignedBy && this.signingAccess == Assignees.dealer) {
                    signedDocs.push(doc.displayName);
                }
            }
        });
        return signedDocs;
    }

    private async getDealInfo(deal?: Deal) {
        if (!deal) {
            deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId ?? this.document?.dealId));
        }

        return deal;
    }

    // Begins process of going through the fields as a stepped process
    async guidedSigning() {
        this.guidedSigningState = await this.pits.advanceGuidedSigning(this.signingAccess);

        // Save Document if,
        //  - Document has been marked as completed
        //  - There are no more fields to complete
        //      - check if no fields left, and if the amount of empty optional checks matches remaining fields
        if (
            this.pits.isDocumentComplete &&
            (!this.guidedSigningState ||
                this.guidedSigningState.fieldsToComplete === 0 ||
                (this.guidedSigningState.fieldsToComplete === this.guidedSigningState.emptyOptionalChecks && !this.guidedSigningState.nextField))
        ) {
            this.submitSignedDocumentAndCheck(true);
        }
    }

    private handleFile$(fileBlob, fileName: string): Observable<any> {
        return iif(
            () =>
                fileBlob?.data?.filename.includes('.pdf') ||
                fileBlob?.data?.filename.includes('.jpg') ||
                fileBlob?.data?.filename.includes('.jpeg') ||
                fileBlob?.data?.filename.includes('.png') ||
                fileBlob?.type?.filename.includes('pdf'),
            this.selectPdf$(fileBlob, fileName),
            this.prepareImage$(fileBlob)
        );
    }

    private hasDealAnnotations(document: DdjDocument): boolean {
        return document && document.inputFieldSettings ? Object.entries(document.inputFieldSettings)?.some(([k, v]) => v?.assignee === 'deal') : false;
    }

    private hasSignatures(document: DdjDocument): boolean {
        return (
            document?.status && !(document.status.buyerSignedBy !== null || document.status.coBuyerSignedBy !== null || document.status.dealerSignedBy !== null)
        );
    }

    private isPdfAnnotationsAndFieldsResetAllowed(): boolean {
        return this.userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.FAndIForms]);
    }

    private async loadDocumentToView(): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, {
            noSignatures: true,
        });

        let signedMetaData: [{ [key: string]: PdfField }] = [{}];

        if (this.document.documentStatusId !== DocumentStatus.Complete && this.document.documentStatusId !== DocumentStatus.Deleted) {
            //additional meta data for doc.
            signedMetaData = await firstValueFrom(this.documentsService.getSignatureMetaData$(this.dealId ?? this.document?.dealId, this.docId));
        }

        this.loadFile$(this.document.fileName)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async () => {
                this.fitMode = FitModes.FitWidth;
                await this.pits.showAnnotations$(this.typeOfCustomer, this.dealAnnotationCheck, signedMetaData).then(async () => {
                    this.pits.pdfTron = await applyFields(this.pits.pdfTron, this.typeOfCustomer, this.isDesktop, signedMetaData);
                    await applyReadOnlyFields(this.pits.pdfTron, await this.getThenSetDealInfo());
                    this.pits.pdfTron = await removeAllAnnotations(this.pits.pdfTron);
                    this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();

                    // if this doc is deleted, slap a big annoying warning on top of it
                    if (DocumentStatus.Deleted === this.document.documentStatusId) {
                        applyWatermark(this.pits.pdfTron, 'DELETED', 'diagonal', 'red', 96, 25);
                    }

                    this.pdfIsReadyShowButtons = true;
                    this.setPdfDisplayMode(this.fitMode);
                    scrollIntoView('#file-actions');
                });
                this.isLoading = false;
            });
    }

    private async loadDocumentToSign(): Promise<void> {
        if (this.userService.userHasMissingSignatures(this.dealId, this.docId)) {
            this.isLoading = false;
            return;
        }

        const isRouteOneDoc = this.vendorService.isDocumentForRouteOne(this.document);

        await this.pits.createWebViewerForEditing(this.pdfViewerRef, {
            loadSignaturePointData: isRouteOneDoc,
            // Turning off as this only removes the field from the document metadata
            // When signing, applyFields removes the annotation, but we still want the
            // metadata on the document
            triggerDeleteEvent: false,
        });

        if (!this.signingAccess) {
            await this.getSigningAccess();
        }

        // Listen for signaturesReady on PITS so we can check if the images are valid
        this.signaturesReadyEventHandler();

        //additional meta data for doc.
        const signedMetaData = await firstValueFrom(this.documentsService.getSignatureMetaData$(this.dealId, this.docId));

        // Verify the fields are being set properly
        if (typeof this.document?.inputFieldSettings === 'string') {
            this.pits.fields = cloneFields(JSON.parse(this.document?.inputFieldSettings || '{}'));
        } else {
            this.pits.fields = this.document?.inputFieldSettings || {};
        }

        this.pits.fieldsBkup = JSON.parse(JSON.stringify(this.pits.fields));
        const isSystemDocument = this.document?.systemDocument || false;
        this.isLoading = true;
        const deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId));
        if (
            this.documentsService.isEnableSigning(this.document, this.signingAccess, signedMetaData) ||
            this.vendorService.isDocumentForRouteOne(this.document)
        ) {
            if (this.isPredefineSignaturesFile()) {
                this.setPredefineSignaturesFile();
            } else {
                this.loadFile$(this.document.fileName)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe(async () => {
                        isSystemDocument
                            ? this.setIsSigningMandatoryDoc(this.signingAccess, this.document, deal, signedMetaData)
                            : this.setIsSigning(this.signingAccess, this.document, deal, signedMetaData);
                        this.enableAfterDocumentLoaded();
                        this.allowGuidedSigning = true;
                    });
            }
        } else {
            if (this.isPredefineSignaturesFile()) {
                this.setPredefineSignaturesFile();
            } else {
                this.loadFile$(this.document.fileName)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe(() => {
                        this.alreadySignedSession = true;
                        this.setIsViewingSigning(this.document, deal, signedMetaData, this.signingAccess);
                        this.enableAfterDocumentLoaded();
                    });
            }
        }
    }

    private signaturesReadyEventHandler() {
        // Listen for signaturesReady so we can check if the images are valid
        let signatureCheckSub: Subscription = null;
        signatureCheckSub = this.pits.signaturesReady
            .asObservable()
            .pipe(takeWhile(() => this.isComponentAlive || !this.invalidSignatures))
            .subscribe((ready) => {
                if (ready) {
                    // If an image is below one of these thresholds there is a chance the image
                    // was uploaded incorrectly to S3
                    const SIGNATURE_SIZE_THRESHOLD = 2000;
                    const INITIALS_SIZE_THRESHOLD = 1000;

                    // Verify the image sizes are appropriate for each signature type
                    if (
                        (this.pits.signature?.data?.length < SIGNATURE_SIZE_THRESHOLD || this.pits.initials?.data?.length < INITIALS_SIZE_THRESHOLD) &&
                        !this.invalidSignatures
                    ) {
                        // reset some stuff to prevent any weird behavior if the user somehow closes dialog
                        this.invalidSignatures = true;
                        // We have a bad signature! Force the user back to re-created them, or re-save them
                        this.userService.routeHasMissingSignaturesError(this.docId, this.dealId, 'brokenSignature');
                        this.isLoading = false;
                    } else if (this.pits.signature?.data?.length > SIGNATURE_SIZE_THRESHOLD && this.pits.initials?.data?.length > INITIALS_SIZE_THRESHOLD) {
                        this.invalidSignatures = false;
                    }
                    if (signatureCheckSub) {
                        signatureCheckSub.unsubscribe();
                    }
                }
            });
    }

    private loadDocumentFromPath() {
        this.loadFile$(this.document.fileName, false)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(() => {
                this.pits.fields = { ...this.fields };
                this.setIsEditing();
                this.enableAfterDocumentLoaded();
            });
    }

    private isPredefineSignaturesFile() {
        return (
            this.document.uploadTypeId === FormDocumentUploadType.Dealer_Uploaded_with_Predefine_Signatures.valueOf() &&
            this.dealerUploadedWithPredefineSignaturesFile !== null
        );
    }

    // No Longer allowing dealer to upload and sign within the same session.
    private setPredefineSignaturesFile() {
        this.selectPdf$(this.dealerUploadedWithPredefineSignaturesFile, this.document.fileName)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async () => {
                // No Longer allowing dealer to upload and sign within the same session.
                this.setIsViewingAllDataWithAnnotations();
                this.enableAfterDocumentLoaded();
                this.allowGuidedSigning = true;
            });
    }

    private enableAfterDocumentLoaded() {
        const { documentViewer } = this.pits.pdfTron.Core;
        documentViewer.addEventListener('documentLoaded', () => {
            this.pdfIsReadyShowButtons = true;
            this.isLoading = false;
        });
    }

    private loadFile$(fileName: string, isTemplate = false) {
        this.setIsErrorState(false);

        if (this.commonDocument) {
            return this.documentsService.getCommonTemplateFileByDocId$(this.docId).pipe(
                takeWhile(() => this.documentsService.requestedDocId === this.docId),
                switchMap((file) => this.handleFile$(file, fileName))
            );
        } else if (this.vendorService.isDocumentForRouteOne(this.document) && this.document.documentStatusId !== DocumentStatus.Complete) {
            return this.selectPdf$(
                {
                    data: {
                        content: this.document.vendorPartyDocumentFile.docBase64,
                    },
                },
                'route-one-document.pdf'
            );
        } else {
            //for some reason Iif no longer worked correctly here after implementing changes to stop api execution
            //for disabled user. Changed to a normal If/else which works correctly again.
            if (isTemplate) {
                return this.documentsService.getSignedTemplateFileByDocId$(this.docId).pipe(
                    takeWhile(() => this.documentsService.requestedDocId === this.docId),
                    switchMap((file) => {
                        return this.handleFile$(file, fileName);
                    })
                );
            } else {
                return this.documentsService.getSignedFileByPath$(this.dealId ?? this.document?.dealId, this.docId).pipe(
                    takeWhile(() => this.documentsService.requestedDocId === this.docId),
                    switchMap((file) => {
                        return this.handleFile$(file, fileName);
                    })
                );
            }
        }
    }

    private async loadWebViewerForDealerUpload(): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, { allowNoSignature: true });

        if (this.template.includes('dealer-create')) {
            this.cleanPdf();
        }

        if (this.fields) {
            if (Object.keys(this.fields).length !== 0) {
                this.pits.fields = this.fields;
            } else {
                this.pits.fields = cloneFields(this.document.inputFieldSettings || {});
            }
        }

        const fileName = this.document.name ? this.document.name : this.document.fileName;
        let extension: 'pdf' | 'jpg' | 'png' = 'pdf';

        if (this.document.type === 'image/jpeg') {
            extension = 'jpg';
        } else if (this.document.type === 'image/png') {
            extension = 'png';
        }

        this.selectPdf$(this.document, fileName)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.close();
                    this.isLoading = false;
                    this.outEvent.emit({
                        context: 'file-error',
                        data: {},
                    });
                    throw error;
                })
            )
            .subscribe(async (val) => {
                //This check is for detecting impact data from pddj.
                if (val === undefined) {
                    await this.pits.showAnnotations$(undefined, undefined, undefined, extension).then(() => {
                        this.pits.pdfTron.Core.annotationManager.disableReadOnlyMode();
                        this.setPdfDisplayMode(FitModes.FitWidth);
                        this.pdfIsReadyShowButtons = true;
                        this.isLoading = false;
                        this.emitEvent('doc-loaded');
                    });
                }
            });
    }

    private async loadWebViewerForDealerEditExistingTemplate(): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, { allowNoSignature: true });

        // fields will be set if the dealer has edited the fields
        if (Object.keys(this.fields).length !== 0 || this.template === 'edited-existing') {
            this.pits.fields = this.fields || {};
        } else {
            this.pits.fields = cloneFields(this.document.inputFieldSettings || {});
        }

        const fileName = this.document.name ? this.document.name : this.document.fileName;
        this.loadFile$(fileName, true)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async () => {
                this.pdfIsReadyShowButtons = true;
                await this.pits.showAnnotations$().then(() => {
                    this.pits.pdfTron.Core.annotationManager.disableReadOnlyMode();
                    this.setPdfDisplayMode(FitModes.FitWidth);
                    this.pdfIsReadyShowButtons = true;
                    this.isLoading = false;
                });
            });
    }

    private async loadWebViewerForDealerViewExistingTemplate(hasPresignedUrl = false): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, { allowNoSignature: true });
        // fields will be set if the dealer has edited the fields
        if (this.template === 'dealer-view-template') {
            this.pits.fields = cloneFields(this.document.inputFieldSettings || {});
        } else {
            this.pits.fields = this.fields || {};
        }

        const fileName = this.document.name ? this.document.name : this.document.fileName;

        //If we already have the url for the file skip loadDocument to selectPDF$.
        if (hasPresignedUrl) {
            this.selectPdf$({ data: { filename: this.document.documentSignedURL } }, fileName)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(async () => {
                    await this.afterTemplateFileLoading();
                });
        } else {
            this.loadFile$(fileName, true)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(async () => {
                    await this.afterTemplateFileLoading();
                });
        }
    }

    private async afterTemplateFileLoading() {
        this.pdfIsReadyShowButtons = true;
        await this.pits.showAnnotations$(this.typeOfCustomer, this.dealAnnotationCheck).then(async () => {
            this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();
            this.setViewExperience();
            scrollIntoView('#file-actions');
            this.isLoading = false;
        });
    }

    private async loadWebViewerForDealerViewCreatedTemplate(): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, { allowNoSignature: true });

        // fields will be set if the dealer has edited the fields
        if (Object.keys(this.fields).length !== 0) {
            this.pits.fields = this.fields;
        } else {
            this.pits.fields = cloneFields(this.document.inputFieldSettings || {});
        }

        const fileName = this.document.name ? this.document.name : this.document.fileName;

        this.selectPdf$(this.document, this.document.name)
            .pipe(
                takeWhile(() => this.isComponentAlive === true),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.close();
                    this.isLoading = false;
                    this.outEvent.emit({
                        context: 'file-error',
                        data: {},
                    });
                    throw error;
                })
            )
            .subscribe(async (val) => {
                //This check is for detecting impact data from pddj.
                if (val === undefined) {
                    await this.pits.showAnnotations$().then(() => {
                        this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();
                        this.setPdfDisplayMode(FitModes.FitWidth);
                        this.pdfIsReadyShowButtons = true;
                        this.isLoading = false;
                        this.emitEvent('doc-loaded');
                    });
                }
            });
    }

    private async loadWebViewerForDealerEditTemplate(): Promise<void> {
        await this.pits.createWebViewerForEditing(this.pdfViewerRef);

        // fields will be set if the dealer has edited the fields
        if (Object.keys(this.fields).length !== 0) {
            this.pits.fields = this.fields;
        } else {
            this.pits.fields = cloneFields(this.document.inputFieldSettings || {});
        }

        const fileName = this.document.name ? this.document.name : this.document.fileName;

        this.loadFile$(fileName, true)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async () => {
                await this.pits.showAnnotations$(this.typeOfCustomer, this.dealAnnotationCheck).then(async () => {
                    this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();
                    this.setViewExperience();
                    scrollIntoView('#file-actions');
                    this.pdfIsReadyShowButtons = true;
                });
            });
        this.isLoading = false;
    }

    private async loadWebViewerForUpload(): Promise<void> {
        if (isIdentification(this.uploadFileDocumentType)) {
            // Apply the raw file data before turning it into a pdf.
            if (!this.document.type.includes('pdf')) {
                this.selectedImg = this.document;
            }
        }

        // Not signing with widgets but still need the signing session meta data.
        if (!this.unauthenticatedUpload) {
            this.getSigningAccess();
        }

        // second parameter prepares WebViewer without signture handling
        await this.pits.createWebViewerForEditing(this.pdfViewerRef, { noSignatures: true });

        this.selectPdf$(this.document, this.document.name)
            .pipe(
                takeWhile(() => this.isComponentAlive === true),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    this.close();
                    this.outEvent.emit({
                        context: 'file-error',
                        data: {},
                    });
                    throw error;
                })
            )
            .subscribe((val) => {
                if (val === undefined) {
                    this.isLoading = false;
                    this.pdfIsReadyShowButtons = true;
                }
            });
    }

    private async loadWebViewerForUnauthenticatedUpload(): Promise<void> {
        if (isIdentification(this.uploadFileDocumentType)) {
            // Apply the raw file data before turning it into a pdf.
            if (!this.document.type.includes('pdf')) {
                this.selectedImg = this.document;
            }
        }
    }

    public openEditPdfView(): void {
        let context;

        if (this.template === 'dealer-view-template' || this.template === 'cancel-existing-edit') {
            context = 'edit-existing';
        } else if (this.template === 'dealer-edited-existing') {
            context = 'edited-existing';
        } else {
            context = 'edit-create';
        }

        // need to pass the fields back when editing existing form
        this.outEvent.emit({
            context,
            data: {
                document: this.document,
                fields: this.pits.fields,
            },
        });
    }

    removeAnnotations(type: 'Free Text' | 'Stamp' | 'Widget') {
        if (this.apiSigningEnabled) {
            const { annotationManager } = this.pits.pdfTron.Core;
            const annotationsList = annotationManager.getAnnotationsList();
            const toRemove = [];

            annotationsList.forEach((annot) => {
                if (annot.Subject === type) {
                    // Stamp will not be removed if set to ReadOnly
                    annot.ReadOnly = false;
                    toRemove.push(annot);
                }
            });

            if (toRemove.length > 0) {
                annotationManager.deleteAnnotations(toRemove);
            }
        }
    }

    //All data needs removed after signing a document.
    //Data is applied via meta data in the api.
    cleanUpDocForSigningSession() {
        //Must have meta data before cleaning
        if (this.pits.signingSessionData) {
            this.removeAnnotations('Free Text');
            this.removeAnnotations('Stamp');
            this.removeAnnotations('Widget');
            //this.removeAllFieldsAndAnnotations();
        }
    }

    /**
     * @deprecated Signing now removes everything in the webviewer. Use prepareFieldsForMetaDataSave()
     */
    private prepareFieldsForSave() {
        // Grab list of current fields and loop over
        const { Annotations, documentViewer } = this.pits.pdfTron.Core;
        const currentList = this.pits.fieldsBkup;
        const annotManager = documentViewer.getAnnotationManager();
        const annotationsList = annotManager.getAnnotationsList();
        const doc = documentViewer.getDocument();
        const toRemove = [];
        const toRemoveFields = [];

        Object.keys(currentList).forEach(async (key) => {
            const current = currentList[key];

            if (current) {
                current.setAsActive = undefined;
                // Check if the field has a widget and potentially signature
                const currentWidget = annotationsList.find(
                    (a) => a.Subject === 'Widget' && (a.fieldName === `widget_${current.name}` || a.fieldName === `widget_deal_${current.name}`)
                );

                if (currentWidget) {
                    // properly set the values on the fields and remove widget from document
                    switch (current.type) {
                        case FieldTypes.signature:
                        case FieldTypes.initials:
                        case FieldTypes.legalName:
                        case FieldTypes.optionalSignature:
                        case FieldTypes.optionalInitials:
                        case FieldTypes.optionalLegalName:
                            // check if there is a signature attached to the widget
                            // eslint-disable-next-line no-case-declarations
                            const signature = annotationsList.find((a: any) => a.Subject === 'Signature' && a.Id === currentWidget.annot?.Id);

                            if (signature && this.vendorService.isDocumentForRouteOne(this.document)) {
                                // TODO: Process signaature for RouteOne processing
                                if (!this.pits.signaturePointData || !this.pits.initialsPointData) {
                                    await this.pits.setSignaturePointData();
                                }

                                current.value = {
                                    x: signature.X,
                                    y: signature.Y,
                                    height: signature.Height,
                                    width: signature.Width,
                                };

                                current.value.value = this.vendorService.createRouteOneData(
                                    current.type === FieldTypes.initials || current.type === FieldTypes.optionalInitials
                                        ? this.pits.initialsPointData
                                        : this.pits.signaturePointData
                                );

                                // Always remove widgets on RouteOne Documents
                                toRemove.push(currentWidget);
                            } else if (signature) {
                                // sever the connection between widget and signature, so we can
                                // remove the widget without removing the signature. This needs
                                // to be done in order to prevent the widget from popping up after
                                // the field has been signed already.
                                currentWidget.annot = undefined;
                                if (!this.apiSigningEnabled) {
                                    toRemove.push(currentWidget);
                                }

                                // Get the PDF coordinates so the API can properly place the signatures to look
                                // identical to what we have when the webviewer loads them in.
                                const pdfCoords = doc.getPDFCoordinates(current.page, signature.X, signature.Y);
                                current.value = {
                                    value: signature.ImageData,
                                    x: signature.X,
                                    pdfX: pdfCoords.x,
                                    y: signature.Y,
                                    pdfY: pdfCoords.y,
                                    height: signature.Height,
                                    width: signature.Width,
                                };

                                if (this.apiSigningEnabled) {
                                    signature.ReadOnly = false;
                                    toRemove.push(signature);
                                }

                                current.signedBy = this.userService.user.userId.toString();
                                current.signedAt = new Date();
                                currentWidget.flags.set('ReadOnly', true);
                                //TODO does this need to be on the documentForm Component
                                currentWidget.innerElement.style.display = 'none';
                            } else {
                                if (!this.apiSigningEnabled) {
                                    toRemove.push(currentWidget);
                                }
                            }
                            break;
                        default:
                            // Account for default values
                            if (currentWidget.value && (currentWidget.value !== 'mm-dd-yyyy' || currentWidget.assignee === 'deal')) {
                                current.value = currentWidget.value.toString();
                                current.signedBy = this.userService.user.userId.toString();
                                current.signedAt = new Date();
                                current.fontSize = currentWidget.getField()?.font?.calculatedSize
                                    ? Math.round(currentWidget.getField().font.calculatedSize as number)
                                    : current.fontSize;
                            } else if (!this.apiSigningEnabled) {
                                toRemove.push(currentWidget);
                            }

                            //All widgets get removed and applied during final signing session in api
                            if (this.featureFlag.isFeatureEnabled(Features.ApiSigningServiceV2)) toRemove.push(currentWidget);
                            break;
                    }
                } else {
                    toRemoveFields.push(current);
                }
            }
        });

        if (toRemove.length > 0) {
            // Remove current fields from document
            annotManager.deleteAnnotations(toRemove, null);
            this.removeAnnotations('Free Text');
            this.removeAnnotations('Stamp');
        }
        this.pits.fields = currentList;
    }

    private mergeAddedFieldsIntoFieldsBkup() {
        //Find the difference
        const difference: { [key: string]: PdfField } = {};

        Object.keys(this.pits.fields).forEach((fKey) => {
            if (!this.pits.fieldsBkup[fKey]) {
                difference[fKey] = this.pits.fields[fKey];
            }
        });

        return { ...difference, ...this.pits.fieldsBkup };
    }

    private prepareFieldsForMetaDataSave() {
        // Grab list of current fields and loop over
        const { Annotations, documentViewer } = this.pits.pdfTron.Core;
        const currentList = this.mergeAddedFieldsIntoFieldsBkup();
        const signingSessionData: { [key: string]: PdfField } = {};
        const annotManager = documentViewer.getAnnotationManager();
        const annotationsList = annotManager.getAnnotationsList();
        const doc = documentViewer.getDocument();
        const toRemove = [];

        Object.keys(currentList).forEach(async (key) => {
            const current = currentList[key];
            if (current) {
                current.setAsActive = undefined;
                // Check if the field has a widget and potentially signature
                const currentWidget = annotationsList.find(
                    (a) => a.Subject === 'Widget' && (a.fieldName === `widget_${current.name}` || a.fieldName === `widget_deal_${current.name}`)
                );

                if (currentWidget) {
                    // properly set the values on the fields and remove widget from document
                    switch (current.type) {
                        case FieldTypes.signature:
                        case FieldTypes.initials:
                        case FieldTypes.legalName:
                        case FieldTypes.optionalSignature:
                        case FieldTypes.optionalInitials:
                        case FieldTypes.optionalLegalName:
                            // check if there is a signature attached to the widget
                            // eslint-disable-next-line no-case-declarations
                            const signature = annotationsList.find((a: any) => a.Subject === 'Signature' && a.Id === currentWidget.annot?.Id);

                            if (signature && this.vendorService.isDocumentForRouteOne(this.document)) {
                                // TODO: Process signature for RouteOne processing
                                if (!this.pits.signaturePointData || !this.pits.initialsPointData) {
                                    await this.pits.setSignaturePointData();
                                }

                                current.value = {
                                    x: signature.X,
                                    y: signature.Y,
                                    height: signature.Height,
                                    width: signature.Width,
                                };

                                current.value.value = this.vendorService.createRouteOneData(
                                    current.type === FieldTypes.initials || current.type === FieldTypes.optionalInitials
                                        ? this.pits.initialsPointData
                                        : this.pits.signaturePointData
                                );

                                signingSessionData[key] = current;
                                // Always remove widgets on RouteOne Documents
                                toRemove.push(currentWidget);
                            } else if (signature) {
                                // sever the connection between widget and signature, so we can
                                // remove the widget without removing the signature. This needs
                                // to be done in order to prevent the widget from popping up after
                                // the field has been signed already.
                                currentWidget.annot = undefined;

                                // Get the PDF coordinates so the API can properly place the signatures to look
                                // identical to what we have when the webviewer loads them in.
                                const pdfCoords = doc.getPDFCoordinates(current.page, signature.X, signature.Y);
                                current.value = {
                                    value: signature.ImageData,
                                    x: signature.X,
                                    pdfX: pdfCoords.x,
                                    y: signature.Y,
                                    pdfY: pdfCoords.y,
                                    height: signature.Height,
                                    width: signature.Width,
                                };

                                if (this.apiSigningEnabled) {
                                    signature.ReadOnly = false;
                                }

                                current.signedBy = this.userService.user.userId.toString();
                                current.signedAt = new Date();
                                currentWidget.flags.set('ReadOnly', true);
                                currentWidget.innerElement.style.display = 'none';

                                signingSessionData[key] = current;
                            } else {
                                toRemove.push(currentWidget);
                            }
                            break;
                        default:
                            // Account for default values
                            if (currentWidget.value && (currentWidget.value !== 'mm-dd-yyyy' || currentWidget.assignee === 'deal')) {
                                current.value = currentWidget.value.toString();
                                current.signedBy = this.userService.user.userId.toString();
                                current.signedAt = new Date();
                                current.fontSize = currentWidget.getField()?.font?.calculatedSize
                                    ? Math.round(currentWidget.getField().font.calculatedSize as number)
                                    : current.fontSize;

                                signingSessionData[key] = current;
                            } else {
                                toRemove.push(currentWidget);
                            }
                    }
                }
            }
        });

        if (toRemove.length > 0) {
            // Remove current fields from document
            annotManager.deleteAnnotations(toRemove, null);
        }

        this.pits.fields = {};
        this.pits.signingSessionData = signingSessionData;
    }

    private prepareImage$(file): Observable<void> {
        return of(null).pipe(
            tap(() => {
                this.selectedImg = file;
                this.img.nativeElement.src = file?.data;
            })
        );
    }

    public print() {
        forkJoin([
            of(this.pits.pdfTron.UI.print()),
            this.documentsService.updateDocumentInfo(this.document, this.document.displayName, this.document.isDealerOnly, 'Print'),
        ]).subscribe();
    }

    private removeAllFieldsAndAnnotations(): void {
        const { documentViewer, annotationManager } = this.pits.pdfTron.Core;
        // Remove any fields that were on the document previously
        const getFieldNameAndValue = (field) => {
            // Do something with data
            const { name, value } = field;

            // Remove any fields that were already included on the document
            annotationManager.deleteAnnotations(field.widgets);

            // Check children fields
            field.children.forEach(getFieldNameAndValue);
        };

        // Remove any initial annotations. Make sure this event listener
        // only gets called once by adding { once: true } as third parameter
        documentViewer.addEventListener(
            'annotationsLoaded',
            () => {
                // Remove the extra annotations first
                const annots = annotationManager.getAnnotationsList();
                annotationManager.deleteAnnotations(annots, { force: true });

                // Secondary clean up. By adding { force: true } it removed the fields properly
                const fieldManager = annotationManager.getFieldManager();
                fieldManager.forEachField(getFieldNameAndValue);
            },
            { once: true }
        );
    }

    public async save(): Promise<void> {
        if (this.template === 'upload') {
            const formData: FormData = new FormData();
            const fileData: FormData = new FormData();
            const signingStatus = await this.setSigningStatus();
            formData.append('signingStatus', JSON.stringify(signingStatus));
            formData.append('ipAddress', this.ipAddress === undefined || this.ipAddress === null ? 'n/a' : this.ipAddress);
            const base64Doc = await prepareFileForUploadBase64(this.pits.pdfTron, false);

            if (base64Doc) {
                if (this.selectedImg) {
                    const base64Img = bufferArrayToBase64(await this.selectedImg.slice(0, this.selectedImg.size, this.selectedImg.type).arrayBuffer());
                    formData.append(
                        'image',
                        JSON.stringify({
                            base64File: base64Img,
                            contentType: this.selectedImg.fileType,
                            fileName: this.fileNameToSave,
                        })
                    );
                }
                formData.append(
                    'file',
                    JSON.stringify({
                        base64File: base64Doc,
                        filename: this.fileNameToSave + '.pdf',
                    })
                );
                formData.append('pdfFields', JSON.stringify(this.pits.fields));
                formData.append(
                    'documentFields',
                    JSON.stringify({
                        documentId: this.docId,
                        fileName: this.fileNameToSave + '.pdf',
                        size: Math.round(base64Doc.replace(/=/g, '').length * 0.75) ?? 0,
                        readyForSignatures: this.readyForSignatures,
                        uploadTypeId: this.uploadTypeId,
                    })
                );
            }

            this.outEvent.emit({
                context: 'image-uploaded',
                data: {
                    fileData,
                    formData,
                },
            });
        } else {
            this.submitSignedDocumentAndCheck();
        }
    }

    private selectPdf$(file, fileName?: string) {
        this.setIsErrorState(false);

        return of(null).pipe(
            tap(() => {
                this.selectedPdf = file;
                this.fileName = fileName || this.selectedPdf?.name;
            }),
            switchMap(() => this.pits.setDocumentBaseline(this.selectedPdf)),
            catchError((error) => {
                this.errorService.showGenericError();
                this.isLoading = false;
                this.close();
                this.outEvent.emit({
                    context: 'file-error',
                    data: {},
                });
                throw error;
            })
        );
    }

    // ImpactImage tells PDFTron that the impact data is coming over as a PNG instead of JSON
    private async setIsEditing(impactData: any = undefined): Promise<void> {
        await this.pits.showAnnotations$().then(() => {
            if (impactData) {
                this.setImpactData(impactData);
            }

            this.pits.pdfTron?.Core?.annotationManager?.disableReadOnlyMode();
            this.setPdfDisplayMode(FitModes.FitWidth);
        });
    }

    private async setIsSigningMandatoryDoc(
        assignee: Assignees,
        document?: DdjDocument,
        deal?: Deal,
        signedMetaData?: [{ [key: string]: PdfField }]
    ): Promise<void> {
        await this.setIsSigning(assignee, document, deal, signedMetaData, true);
    }

    private async setIsSigning(
        assignee: Assignees,
        document?: DdjDocument,
        deal?: Deal,
        signedMetaData?: [{ [key: string]: PdfField }],
        mandatoryDoc: boolean = false
    ): Promise<void> {
        // Should not have system documents go here.
        // Reload annotations in edit mode
        if (assignee || mandatoryDoc) {
            this.pits.showAnnotations$(assignee, this.dealAnnotationCheck, signedMetaData).then(async () => {
                this.pits.pdfTron.Core.annotationManager.disableReadOnlyMode();
                this.pits.pdfTron = await applyFields(this.pits.pdfTron, assignee, this.isDesktop, signedMetaData);
                deal = await this.getThenSetDealInfo(deal);
                const readOnlyFields = await applyReadOnlyFields(this.pits.pdfTron, deal);
                this.updatePits(readOnlyFields.updatePitsFields);
                handleFieldsVisibility(this.pits.pdfTron, assignee, signedMetaData, true);
                handlePreviousSigningWithSignatureMetaData(this.pits.pdfTron, signedMetaData, assignee);

                this.setViewExperience();
            });
        }
    }

    private updatePits(updatePitsFields: { [key: string]: PdfField }) {
        this.pits.fields = { ...this.pits.fields, ...cloneFields(updatePitsFields) };
    }

    public async setIsViewingAllDataWithAnnotations(): Promise<void> {
        this.isLoading = true;

        await this.pits.showAnnotations$(null, true).then(async () => {
            this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();
            this.setViewExperience();
            this.isLoading = false;
        });
    }

    public async setIsViewing(data: any): Promise<void> {
        this.isLoading = true;

        const assignee = data.assignee ? data.assignee : null;
        const removeAnnots: boolean = data.removeAnnots ? data.removeAnnots : false;

        await this.pits.showAnnotations$(assignee, true);

        if (removeAnnots) {
            this.pits.pdfTron = await removeAllAnnotations(this.pits.pdfTron);
        }

        if (data?.impact) {
            this.setImpactData(data.impact);
        }

        this.pits.pdfTron?.Core?.annotationManager?.enableReadOnlyMode();
        this.setPdfDisplayMode(FitModes.FitWidth);
        this.isLoading = false;
    }

    private async setIsViewingSigning(document?: DdjDocument, deal?: Deal, signedMetaData?: [{ [key: string]: PdfField }], assignee: Assignees = undefined) {
        this.pits.showAnnotations$(undefined, this.dealAnnotationCheck).then(async () => {
            deal = await this.getThenSetDealInfo(deal);
            const readOnlyFields = await applyReadOnlyFields(this.pits.pdfTron, deal);
            this.updatePits(readOnlyFields.updatePitsFields);
            // Remove any extra annotations as we only want to dispaly readOnly fields and signatures
            this.pits.pdfTron = await removeAllAnnotations(this.pits.pdfTron);

            handleFieldsVisibility(this.pits.pdfTron, assignee, signedMetaData);
            handlePreviousSigningWithSignatureMetaData(this.pits.pdfTron, signedMetaData, assignee);

            this.pits.pdfTron.Core.annotationManager.enableReadOnlyMode();
            this.pits.pdfTron.UI.setToolMode('Pan');
            if (this.documentsService.assigneeHasSignAndStatusIsTheirQueue(document, assignee, signedMetaData)) {
                this.errorService
                    .showErrorAtService('This document has already been signed by you. We will reprocess the document now.')
                    .afterClosed()
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((val) => {
                        // redirect to deal list after submitting
                        this.zone.run(() => this.submitSignedDocumentAndCheck(false));
                    });
            }
        });
    }

    public setPdfDisplayMode(fitMode: FitModes) {
        this.pits.pdfTron?.UI?.setFitMode(fitMode);
        if (fitMode === FitModes.Zoom) {
            this.pits.pdfTron?.UI?.setZoomLevel(1);
        } else {
            this.fitMode = fitMode;
        }
    }

    private async setSigningStatus(): Promise<object> {
        const userAgent = this.deviceService.getDeviceInfo().userAgent;

        switch (this.signingAccess) {
            case Assignees.buyer:
                return {
                    ...this.document.status,
                    buyerSignedBy: this.userService.user.userId,
                    buyerSignedDate: this.dateTime,
                    buyerSignedDeviceId: userAgent ? userAgent : null,
                    buyerSignedIpAddress: this.ipAddress ? this.ipAddress : null,
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                    signedByEmail: this.userService.user.email,
                    signedLocation: this.ipAddress,
                };
            case Assignees.cobuyer:
                return {
                    ...this.document.status,
                    coBuyerSignedBy: this.userService.user.userId,
                    coBuyerSignedDate: this.dateTime,
                    coBuyerSignedDeviceId: userAgent ? userAgent : null,
                    coBuyerSignedIpAddress: this.ipAddress ? this.ipAddress : null,
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                    signedByEmail: this.userService.user.email,
                    signedLocation: this.ipAddress,
                };
            case Assignees.dealer:
                return {
                    ...this.document.status,
                    dealerSignedBy: this.userService.user.userId,
                    dealerSignedDate: this.dateTime,
                    dealerSignedDeviceId: userAgent ? userAgent : null,
                    dealerSignedIpAddress: this.ipAddress ? this.ipAddress : null,
                    signedByRequest: this.signingAccess,
                    signedById: this.userService.user.userId,
                    signedByEmail: this.userService.user.email,
                    signedLocation: this.ipAddress,
                };
            default:
                return null;
        }
    }

    private setViewExperience(setPanMode = true, setFitMode = true) {
        if (setPanMode) {
            this.pits.pdfTron.UI?.setToolMode('Pan');
        }

        if (isMobileOrTablet() && setFitMode) {
            this.pits.pdfTron.UI?.setFitMode('FitWidth');
        }

        if (setFitMode) {
            this.pits.pdfTron.UI?.setFitMode('FitWidth');
        }
    }

    async submitSignedDocumentAndCheck(routeToNext = true) {
        const formData: FormData = new FormData();
        const fileData: FormData = new FormData();

        if (this.submitDocumentFirstStep()) {
            //Order of operations matters
            await this.submitDocumentSecondStep();
            await this.submitDocumentThirdStep(formData);
            await this.submitDocumentForthStep();
            await this.submitDocumentFifthStep(formData);

            this.outEvent.emit({
                context: 'sign',
                data: {
                    fileData,
                    formData,
                    apiSigningEnabled: this.apiSigningEnabled,
                    routeToNext,
                },
            });
        }
    }

    private submitDocumentFirstStep(): boolean {
        // Verify user signed all required fields
        const valid = this.verifyFieldInputs(this.signingAccess);

        if (!valid) {
            this.errorService.showError({
                statusCode: 500,
                message: 'Please complete all signatures and fields before saving.',
                title: 'Signatures Not Completed',
            });
            return valid;
        }

        // Added to give the user the re-assurance we are doing something
        // as some of the logic in this call can take a couple seconds to complete
        this.outEvent.emit({
            context: 'loading',
            data: {
                loading: true,
            },
        });

        return valid;
    }

    private async submitDocumentSecondStep() {
        // Removes temporary StampAnnotations for signature appearance
        this.removeAnnotations('Stamp');
        // Add the signatures and values to the right annotations
        this.prepareFieldsForMetaDataSave();
        //this.prepareFieldsForSave();

        await this.getSigningAccess();
    }

    private async submitDocumentThirdStep(formData: FormData) {
        formData.append('ipAddress', this.ipAddress === undefined || this.ipAddress === null ? 'n/a' : this.ipAddress);

        const documentData = {
            dealerId: (await this.getDealInfo())?.dealerId ?? -2,
            documentId: this.docId,
            dealId: this.dealId,
            readyForSignatures: this.readyForSignatures,
            fileName: this.document.fileName,
            size: this.document.size,
            inputFieldSettings: this.document.inputFieldSettings,
            documentLastUpdateDate: this.document.documentLastUpdateDate, //for the time being. needed to compare last server date with this.dateTime in api check. 4/4/24
        };

        //For Doc Type 6 need to save pdf field changes before removing them from the document.
        formData.append('pdfFields', JSON.stringify(this.pits.signingSessionData));
        formData.append('documentData', JSON.stringify(documentData));

        // Add in signingStatus
        // Only add signing status if NOT Document Type 6
        if (!this.isPredefineSignaturesFile()) {
            const signingStatus = await this.setSigningStatus();
            formData.append('signingStatus', JSON.stringify(signingStatus));
        }
        // else be sure to remove annots that where shown on the document type 6.
        else {
            console.log('Doc Type 6 Skipping removal of annots.');
        }
    }

    private async submitDocumentForthStep() {
        await this.cleanUpDocForSigningSession();
    }
    private async submitDocumentFifthStep(formData: FormData) {
        const base64 = await prepareFileForUploadBase64(this.pits.pdfTron, false);
        formData.append('pdf', JSON.stringify({ file: base64, type: 'application/pdf' }));
    }

    private isTestingPdf(assignee?: Assignees, impactData?: any): void {
        this.pits.showAnnotations$(assignee, true).then(async () => {
            this.pits.pdfTron?.Core?.annotationManager?.disableReadOnlyMode();
            this.pits.pdfTron = await applyFields(this.pits.pdfTron, assignee, this.isDesktop);

            if (impactData) {
                this.setImpactData(impactData);
            }

            let deal: Deal;
            if (this.dealId) {
                deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId));
            }

            await applyReadOnlyFields(this.pits.pdfTron, deal, true);
            this.setViewExperience(false, true);
        });
    }

    private verifyFieldInputs(assignee: Assignees): boolean {
        const { documentViewer } = this.pits.pdfTron.Core;
        const currentList = this.pits.fieldsBkup;
        const annotManager = documentViewer.getAnnotationManager();
        const annotationsList = annotManager.getAnnotationsList();
        let needsUserAction = [];
        let valid = true;

        Object.keys(currentList).forEach(async (key) => {
            const current = currentList[key];

            if (current) {
                // Check if the field has a widget and potentially signature
                const currentWidget = annotationsList.find((a) => a.Subject === 'Widget' && a.fieldName === `widget_${current.name}`);

                if (currentWidget && current.assignee === assignee) {
                    // properly set the values on the fields and remove widget from document
                    let fieldElement = undefined;
                    switch (current.type) {
                        case FieldTypes.signature:
                        case FieldTypes.initials:
                        case FieldTypes.legalName:
                            // check if there is a signature attached to the widget
                            // eslint-disable-next-line no-case-declarations
                            const signature = annotationsList.find((a: any) => a.Subject === 'Signature' && a.Id === currentWidget.annot?.Id);

                            fieldElement = currentWidget.element.querySelector('.sign-here');

                            if (!signature) {
                                needsUserAction.push(currentWidget);
                                valid = false;
                                fieldElement.classList.add('sign-here_required');
                            } else {
                                fieldElement.classList.remove('sign-here_required');
                            }
                            break;
                        default:
                            fieldElement = currentWidget.element.querySelector('.text-field');
                            // Account for default values
                            if (
                                (!currentWidget.value || currentWidget.value === 'mm-dd-yyyy') &&
                                current.type !== FieldTypes.optionalText &&
                                current.type !== FieldTypes.optionalInitials &&
                                current.type !== FieldTypes.optionalLegalName &&
                                current.type !== FieldTypes.optionalSignature
                            ) {
                                needsUserAction.push(currentWidget);
                                valid = false;
                                fieldElement.classList.add('text-field_MISSED');
                            } else if (fieldElement) {
                                fieldElement.classList.remove('text-field_MISSED');
                            }
                            break;
                    }
                }
            }
        });

        if (needsUserAction.length >= 1) {
            this.pits.annotationFlow$(annotManager, needsUserAction[0]);
            needsUserAction = [];
        }

        return valid;
    }

    public tempDisableType6(): boolean {
        //6 = Dealer Uploaded with Predefine Signatures
        return this.document?.uploadTypeId === 6;
    }

    public tempShouldDisableMatTooltip(): boolean {
        if (!this.isDocumentReadyForSigning) {
            return false;
        }

        return !this.tempDisableType6();
    }

    public tempDisableType6Text(): string {
        let message;
        if (this.tempDisableType6()) {
            message =
                this.document?.uploadTypeId === 6
                    ? '"Dealer Uploaded with PredefineSignatures" type is disabled temporarily. To continue, navigate to "F&I Forms" on the left hand side. Update the type of document to "Form Ready For Signatures".'
                    : '"Manually Filled During the Deal" type is disabled temporarily. To continue, navigate to "F&I Forms" on the left hand side. Update the type of document to "Form Ready For Signatures".';
        } else {
            //Current Default Message
            message = 'This document requires data to be loaded prior to saving. Please add data before signing.';
        }

        return message;
    }

    public async setImpactData(impactData: any) {
        const instance = await applyImpactFields(this.pits.pdfTron, impactData, this.document);
        if (instance) {
            this.pits.pdfTron = instance;
        }
    }

    private handleError(response) {
        // 550 is a custom error type. This will redirect to the deals page.
        this.isLoading = false;
        if (response?.status === 550) {
            const errorMessageDetails = response.error;
            if (errorMessageDetails) {
                errorMessageDetails.title = errorMessageDetails.name;
                this.errorService
                    .showErrorAtService(errorMessageDetails)
                    .afterClosed()
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((val) => {
                        this.outEvent.emit({
                            context: 'sign',
                            data: {
                                fileData: null,
                                formData: null,
                                apiSigningEnabled: this.apiSigningEnabled,
                                routeToNext: false,
                                error: true,
                            },
                        });
                    });
            } else {
                //fallback error message
                this.errorService.showGenericError();
            }
        }
        return;
    }
}
